import type { ReactNode } from "react";

// Three patient situations — the moment a denial letter lands. Sets
// the emotional stakes before the product explains itself.
interface Case {
  label: string;
  story: ReactNode;
}

const CASES: Case[] = [
  {
    label: "Tuesday, 11:47 pm",
    story: (
      <>
        &ldquo;I got home from a twelve-hour shift and there&apos;s a $2,800 bill for the MRI my
        doctor ordered. The letter says <Mark>not medically necessary</Mark>. I have no idea what
        that means or what to do.&rdquo;
      </>
    ),
  },
  {
    label: "Saturday morning",
    story: (
      <>
        &ldquo;My son needs the medication his pediatrician prescribed. The insurer said he has to{" "}
        <Mark>try cheaper drugs first</Mark>. We already did that two years ago. Where do I even
        start?&rdquo;
      </>
    ),
  },
  {
    label: "Three weeks after surgery",
    story: (
      <>
        &ldquo;They covered the surgery but not the anesthesiologist. I&apos;m being told I owe{" "}
        <Mark>$4,100 for someone I never met</Mark>. I don&apos;t know how to argue with a 30-page
        policy.&rdquo;
      </>
    ),
  },
];

function Mark({ children }: { children: ReactNode }) {
  return (
    <em className="bg-[linear-gradient(transparent_55%,rgb(var(--color-brick-soft))_55%)] px-0.5 font-medium not-italic text-ink">
      {children}
    </em>
  );
}

export function EmotionalAnchor() {
  return (
    <section
      className="border-y-[0.5px] border-line bg-parchment-deep px-6 py-28 max-[980px]:py-[72px] sm:px-8"
      aria-label="Who this is for"
    >
      <div className="lp-reveal mx-auto max-w-[880px]">
        <span className="eyebrow">A note for the person reading this</span>
        <p className="my-8 mb-12 mt-8 max-w-[720px] font-serif text-[24px] font-normal leading-[1.5] tracking-[-0.012em] text-ink max-[980px]:text-[19px]">
          A denial letter does its damage on both ends — for the patient who can&apos;t make sense of
          it, and for the clinician who can&apos;t fix care that depends on it. The wording is
          hostile, the deadlines feel arbitrary, the implication is that{" "}
          <em className="italic text-ink">someone official has decided</em>. So most letters end
          up in a drawer. We built this for the moments below.
        </p>

        <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
          {CASES.map((c) => (
            <div key={c.label} className="border-t border-ink pt-[18px]">
              <div className="mb-3 font-mono text-[10px] uppercase tracking-[0.14em] text-brick">
                {c.label}
              </div>
              <p className="font-serif text-[16px] italic leading-[1.55] tracking-[-0.005em] text-ink">
                {c.story}
              </p>
            </div>
          ))}
        </div>

        <p className="mt-14 text-center font-serif text-[19px] font-medium leading-[1.55] tracking-[-0.008em] text-ink">
          If you recognize any of those — or if you&apos;re the person trying to help someone through
          one — you&apos;re in the right place.
        </p>
      </div>
    </section>
  );
}
